import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Form } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Appearance, Design, Size } from '@Components/designConstants';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { useSetError } from '../../../redux/modals';
import { useCommentsUrl } from '../comments-context';
import { DigitalAuditComment } from '../comments-constants';
import { useAddComment, useComments } from '../use-comments';

interface CommentThreadProps {
  // Stable section key - see CommentAnchor in comments-constants.ts.
  anchor: string;
}

/**
 * Collapsible comment thread for one report section. Comments belong to the
 * domain (shared by everyone who audits it), are stamped server-side with
 * author + time, and are read from one per-domain query shared by all threads.
 */
export const CommentThread = ({ anchor }: CommentThreadProps) => {
  const url = useCommentsUrl();
  const commentsQuery = useComments(url);
  const [open, setOpen] = useState(false);

  if (!url) return undefined;

  const comments = (commentsQuery.data ?? []).filter(c => c.anchor === anchor);

  return (
    <div className='digital-audit-comment-thread'>
      <Button
        appearance={Appearance.OUTLINED}
        design={Design.NEUTRAL}
        size={Size.SM}
        onClick={() => setOpen(v => !v)}
      >
        {comments.length > 0
          ? conditionallyPluralizeWithCount(comments.length, 'comment')
          : 'Add a comment'}
      </Button>
      {open && <ThreadBody url={url} anchor={anchor} comments={comments} />}
    </div>
  );
};

interface ThreadBodyProps {
  url: string;
  anchor: string;
  comments: DigitalAuditComment[];
}

const ThreadBody = ({ url, anchor, comments }: ThreadBodyProps) => {
  const setError = useSetError();
  const addComment = useAddComment(url);
  const [draft, setDraft] = useState('');

  const handlePost = async () => {
    try {
      await addComment.mutateAsync({ anchor, body: draft.trim() });
      setDraft('');
      Mixpanel.track(MxE.RecordCreated, { page: 'Digital Audit', entity: 'comment' });
    } catch (e) {
      setError({ message: `Couldn't post your comment: ${e.message}`, reportError: e });
    }
  };

  return (
    <div className='digital-audit-comment-body'>
      {comments.map(comment => (
        <CommentEntry key={comment.id} comment={comment} />
      ))}
      <Form.Control
        as='textarea'
        rows={2}
        placeholder='Write a comment...'
        value={draft}
        onChange={e => setDraft(e.target.value)}
      />
      <Button
        disabled={draft.trim().length === 0}
        loading={addComment.isPending}
        size={Size.SM}
        onClick={handlePost}
      >
        Comment
      </Button>
    </div>
  );
};

interface CommentEntryProps {
  comment: DigitalAuditComment;
}

const CommentEntry = ({ comment }: CommentEntryProps) => (
  <div className='digital-audit-comment'>
    <div className='digital-audit-comment-meta'>
      {comment.author_email} · {format(parseISO(comment.created_at), 'MMM d, yyyy h:mm a')}
    </div>
    <div className='digital-audit-comment-text'>{comment.body}</div>
  </div>
);
